import axios from 'axios'
import { pick } from 'lodash'
import { selectUser } from '../../reducers/users'
import {
  getApiRequest,
  postApiRequest,
  putApiRequest,
  request,
} from '../../helpers/apiHelper'
import { getApiErrorMessage } from '../../helpers/apiErrorHelper'
import { displayNotification } from '../../actions/notificationsActions'
import {
  createUserMutation,
  handoffUserMutation,
  createRefiMutation,
  updateRefiMutation,
  updateRefiTermMutation,
  addRefiProductMutation,
  verifyOwnershipMutation,
  deferOwnershipMutation,
} from './queries'

import { fetchBusinessConfig } from '../common/config'

export const setRefiAppCreditStepState =
  (creditApplicationStep) => (dispatch) => {
    dispatch({
      type: 'SET_REFI_APPLICATION_DATA',
      payload: {
        creditApplicationStep,
      },
    })
  }

export const setRefiAppRejectedCreditAppState =
  (rejectedCreditApplication) => (dispatch) => {
    dispatch({
      type: 'SET_REFI_APPLICATION_DATA',
      payload: {
        rejectedCreditApplication,
      },
    })
  }

export const triggerVinRequiredError = () => (dispatch) => {
  dispatch({ type: 'VIN_REQUIRED_ERROR' })
}

export const clearVinRequiredError = () => (dispatch) => {
  dispatch({ type: 'CLEAR_VIN_REQUIRED_ERROR' })
}

export const profileSectionComplete = (refi, dispatch) => {
  if (refi.status === 'QUALIFIED') {
    dispatch({
      type: 'SET_APPLICATION_STATE',
      payload: {
        showProfile: false,
        showUnderwriting: true,
      },
    })
  }
}

export const checkProducts = (refi, dispatch) => {
  if (refi.selectableProducts.length <= 0) {
    dispatch({
      type: 'SET_REFI_APPLICATION_DATA',
      payload: {
        finalize: true,
      },
    })
  }
}

export const resetCurrentApplication = () => (dispatch) => {
  dispatch({ type: 'RESET_REFI_APPLICATION_UI_STATE' })
  dispatch({ type: 'RESET_REFI_APPLICATION_DATA' })
  dispatch({ type: 'RESET_COVERAGE_DETAILS_STATE' })
}

export const findOrCreateVehicle = (vehicle) => (dispatch) => {
  return axios
    .post(
      `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/vehicles`,
      vehicle
    )
    .then((response) => {
      const vehicle = response.data
      dispatch({
        type: 'FETCH_VEHICLE_SUCCESS',
        entity: vehicle,
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          vehicleId: vehicle.id,
        },
      })

      return Promise.resolve(vehicle)
    })
    .catch((error) => {
      const errorMessage = getApiErrorMessage(error, 'Something went wrong')
      dispatch({
        type: 'FETCH_VEHICLE_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const findOrCreateGarageEntry = (garageEntry) => (dispatch) => {
  return axios
    .post(
      `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/garage`,
      garageEntry
    )
    .then((response) => {
      const garageEntry = response.data
      dispatch({
        type: 'FETCH_GARAGE_ENTRY_SUCCESS',
        entity: garageEntry,
      })
    })
    .catch((error) => {
      const errorMessage = getApiErrorMessage(error, 'Something went wrong')
      dispatch({
        type: 'FETCH_GARAGE_ENTRY_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const createUser = (user) => (dispatch) => {
  return request(createUserMutation, { user })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const user = response.data.data.createUser.user
        dispatch({
          type: 'CREATE_USER',
          entity: user,
        })
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            userId: user.id,
            addressId: user.addresses[user.addresses.length - 1].id,
          },
        })
        return user
      }
    })
    .catch((error) => {
      dispatch({
        type: 'CREATE_USER_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const handoffUser = (user) => (dispatch) => {
  return request(handoffUserMutation, { user }).then(
    (response) => {
      if (response.data.errors) {
        dispatch(
          displayNotification(`${response.data.errors[0].message}`, 'error')
        )
      } else {
        const user = response.data.data.handoffUser.user
        dispatch(resetCurrentApplication())
        dispatch(
          displayNotification(
            `Handoff success! Email has been sent to ${user.email}`
          )
        )
      }
    },
    (error) => {
      dispatch({
        type: 'UPDATE_USER_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
    }
  )
}

export const createRefi = (refi) => (dispatch) => {
  return request(createRefiMutation, { refi })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const refi = response.data.data.createRefi.refi
        dispatch({
          type: 'CREATE_REFI',
          entity: refi,
        })
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            refiId: refi.id,
            creditApplicationStep: 'SelectTradeline',
          },
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'CREATE_REFI_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const updateRefi = (refi) => (dispatch) => {
  return putApiRequest(`refis/${refi.id}`, { ...refi })
    .then((response) => {
      const refi = response.data
      dispatch({
        type: 'UPDATE_REFI_SUCCESS',
        entity: refi,
      })
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const updatePaymentOptions = (refi) => (dispatch) => {
  return request(updateRefiMutation, { refi })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const refi = response.data.data.updateRefi.refi
        dispatch({
          type: 'UPDATE_REFI_SUCCESS',
          entity: refi,
        })
        checkProducts(refi, dispatch)
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const updateRefiTradeline = (refi) => (dispatch) => {
  return request(updateRefiMutation, { refi })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const refi = response.data.data.updateRefi.refi
        dispatch({
          type: 'UPDATE_REFI_SUCCESS',
          entity: refi,
        })
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            manualTradeline: false,
          },
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const createRefiProducts = (refi) => (dispatch) => {
  return postApiRequest(`refis/${refi.id}/products`)
    .then((response) => {
      const products = response.data.products
      dispatch({
        type: 'UPDATE_REFI_SUCCESS',
        entity: { id: refi.id, products },
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          finalize: true,
        },
      })
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(errorMessage, 'error'))
      throw new Error(error)
    })
}

export const updateRefiProducts = (refi) => (dispatch) => {
  return putApiRequest(`refis/${refi.id}/products`, refi)
    .then((response) => {
      const { products } = response.data
      dispatch({
        type: 'UPDATE_REFI_SUCCESS',
        entity: { id: refi.id, products },
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          finalize: true,
        },
      })
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(errorMessage, 'error'))
      throw new Error(error)
    })
}

export const createRefiStipulation =
  ({ id, stipulation }) =>
  (dispatch) => {
    return postApiRequest(`refis/${id}/stipulations`, { stipulation })
      .then(() => {
        dispatch({
          type: 'CREATE_REFI_STIPULATION',
        })
      })
      .catch((error) => {
        const errorMessage =
          error.response && error.response.data
            ? error.response.data.message
            : 'Something went wrong'
        dispatch({
          type: 'CREATE_REFI_STIPULATION_FAILURE',
        })
        dispatch(displayNotification(errorMessage, 'error'))
        throw new Error(error)
      })
  }

export const updateRefiStipulation = (stipulation) => (dispatch) => {
  return postApiRequest(`/stipulations/${stipulation.id}`, stipulation)
    .then(() => {
      dispatch({
        type: 'UPDATE_REFI_STIPULATION',
      })
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'UPDATE_REFI_STIPULATION_FAILURE',
      })
      dispatch(displayNotification(errorMessage, 'error'))
      throw new Error(error)
    })
}

export const acceptRefi = (refi) => (dispatch) => {
  return putApiRequest(`refis/${refi.id}/accept`, refi)
    .then((response) => {
      const refi = response.data
      dispatch({
        type: 'REFI_ACCEPTED',
        entity: refi,
      })
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(errorMessage, 'error'))
      throw new Error(error)
    })
}

export const updateRefiLoanTerm = (updateRefiTerm) => (dispatch) => {
  return request(updateRefiTermMutation, { updateRefiTerm })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const refi = response.data.data.updateRefiTerm.refi
        dispatch({
          type: 'UPDATE_REFI_TERM_SUCCESS',
          entity: refi,
        })
        dispatch(
          displayNotification('Loan Term changed succesfully', 'success')
        )
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const addRefiProduct = (addRefiProduct) => (dispatch) => {
  return request(addRefiProductMutation, { addRefiProduct })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const refi = response.data.data.addRefiProduct.refi
        dispatch({
          type: 'ADD_REFI_PRODUCT_SUCCESS',
          entity: refi,
        })
        dispatch(displayNotification('Product added succesfully', 'success'))
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_REFI_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const optinRefiMembership = (refiId) => (dispatch) => {
  return putApiRequest(`refis/${refiId}/opt_in`, null)
    .then(() => {
      dispatch({
        type: 'OPT_IN_REFI_SUCCESS',
      })
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'OPT_IN_REFI_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const getRefiPaymentOptions = (refi) => (dispatch) => {
  return getApiRequest(
    `refis/${refi.id}/payment_options?cash_back_amount=${refi.cash_back_amount}`
  )
    .then((response) => {
      const paymentOptions = response.data.payment_options
      dispatch({
        type: 'FETCH_PAYMENT_OPTIONS_SUCCESS',
        paymentOptions,
      })
      return refi.paymentOptions
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'FETCH_REFI_PAYMENT_OPTIONS_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const getRefi = (refiId) => (dispatch) => {
  return getApiRequest(`refis/${refiId}`)
    .then((response) => {
      const { refi } = response.data.payload
      dispatch({
        type: 'FETCH_REFI_SUCCESS',
        entity: refi,
      })
      return response.data
    })
    .catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong'
      dispatch({
        type: 'FETCH_REFI_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const updateVehicle = (vehicle) => (dispatch) => {
  return axios
    .put(
      `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/vehicles/${vehicle.id}`,
      vehicle
    )
    .then((response) => {
      const vehicle = response.data
      dispatch({
        type: 'UPDATE_VEHICLE_SUCCESS',
        entity: vehicle,
      })
      dispatch({
        type: 'SET_APPLICATION_STATE',
        payload: {
          showVehicle: !vehicle.refinanceable,
          showProfile: vehicle.refinanceable,
        },
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          editVehicleDetails: false,
        },
      })
    })
    .catch((error) => {
      const errorMessage = getApiErrorMessage(error, 'Something went wrong')
      dispatch({
        type: 'UPDATE_VEHICLE_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const verifyOwnership = (vehicleId, userId) => (dispatch) => {
  return request(verifyOwnershipMutation, {
    ownership: { vehicleId, userId },
  })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const owns = response.data.data.verifyOwnership.ownership.owns
        const manualVerification =
          response.data.data.verifyOwnership.ownership.verificationMethod ===
          'MANUAL'
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            owns,
            manualVerification,
          },
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'VERIFY_OWNERSHIP_FAILURE',
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          vehicleId: null,
          userId: null,
          owned: null,
          manualVerification: null,
        },
      })
      dispatch({
        type: 'SET_APPLICATION_STATE',
        payload: {
          showVehicle: true,
          showProfile: false,
        },
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const deferOwnershipCheck = (vehicleId, userId) => (dispatch) => {
  return request(deferOwnershipMutation, {
    deferOwnership: { vehicleId, userId },
  })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const owns = response.data.data.deferOwnership.ownership.owns
        const manualVerification =
          response.data.data.deferOwnership.ownership.verificationMethod ===
          'MANUAL'
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            owns,
            manualVerification,
          },
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'SET_APPLICATION_STATE',
        payload: {
          showVehicle: true,
          showProfile: false,
        },
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const deleteCurrentApplicationVehicle = () => (dispatch) => {
  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: {
      vehicleId: null,
    },
  })
}

export const setEditVehicleDetailsTrue = () => (dispatch) => {
  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: {
      editVehicleDetails: true,
    },
  })
}

export const setRefiAppSelectedTradelineIndex =
  (tradelineIndex) => (dispatch) => {
    dispatch({
      type: 'SET_REFI_APPLICATION_DATA',
      payload: {
        selectedTradelineIndex: tradelineIndex,
      },
    })
  }

export const setRefiAppManualTradelineTrue = () => (dispatch) => {
  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: {
      manualTradeline: true,
    },
  })
}

export const toggleVehicleUiState = () => (dispatch) => {
  dispatch({
    type: 'TOGGLE_VEHICLE_UI_STATE',
  })
}

export const toggleProfileUiState = () => (dispatch) => {
  dispatch({
    type: 'TOGGLE_PROFILE_UI_STATE',
  })
}

export const toggleUnderwritingUiState = () => (dispatch) => {
  dispatch({
    type: 'TOGGLE_UNDERWRITING_UI_STATE',
  })
}

export const toggleCoverageUiState = () => (dispatch) => {
  dispatch({
    type: 'TOGGLE_COVERAGE_UI_STATE',
  })
}

export const toggleCreditUiState = () => (dispatch) => {
  dispatch({
    type: 'TOGGLE_CREDIT_UI_STATE',
  })
}

// Opens the Personal Details (Profile) section and closes Vehicle Details
export const openProfileSection = () => (dispatch) => {
  dispatch({
    type: 'SET_APPLICATION_STATE',
    payload: {
      showVehicle: false,
      showProfile: true,
      showUnderwriting: false,
      showCoverage: false,
      showCredit: false,
    },
  })
}

/**
 * Opens the Vehicle Details section (closing all other sections)
 * @returns {void}
 */
export const openVehicleSection = () => (dispatch) => {
  dispatch({
    type: 'SET_APPLICATION_STATE',
    payload: {
      showVehicle: true,
      showProfile: false,
      showUnderwriting: false,
      showCoverage: false,
      showCredit: false,
      coverageStep: 'Membership',
      creditSectionFinished: false,
    },
  })
}

export const openVehicleSectionPreserveProgress = () => (dispatch) => {
  dispatch({
    type: 'SET_APPLICATION_STATE',
    payload: {
      showVehicle: true,
      showProfile: false,
      showUnderwriting: false,
      showCoverage: false,
      showCredit: false,
    },
  })
}

export const openCoverageSection = () => (dispatch) => {
  dispatch({
    type: 'SET_APPLICATION_STATE',
    payload: {
      showVehicle: false,
      showProfile: false,
      showUnderwriting: false,
      showCoverage: true,
      showCredit: false,
    },
  })
}

export const goToCoverageDetailsSection = () => (dispatch) => {
  dispatch({
    type: 'GO_TO_COVERAGE_DETAILS_SECTION',
  })
}

export const setCreditSessionFinished = () => (dispatch) => {
  dispatch({
    type: 'CREDIT_SECTION_FINISHED',
  })
}

export const setCreditSessionNotFinished = () => (dispatch) => {
  dispatch({
    type: 'CREDIT_SECTION_NOT_FINISHED',
  })
}

export const coverageDetailsNextStep = () => (dispatch) => {
  dispatch({
    type: 'COVERAGE_DETAILS_NEXT_STEP',
  })
  dispatch({
    type: 'SET_APPLICATION_STATE',
    payload: { showVehicle: false, showProfile: false },
  })
}

export const coverageDetailsPreviousStep = () => (dispatch) => {
  dispatch({
    type: 'COVERAGE_DETAILS_PREVIOUS_STEP',
  })
  dispatch({
    type: 'RESET_PRODUCT_PACKAGE_PAYMENT_OPTIONS',
  })
}

export const setSelectedTerms = (payload) => ({
  type: 'SET_SELECTED_TERMS',
  payload,
})

export const syncRefiVehicle = (refiData, dispatch) => {
  const { vehicle } = refiData
  dispatch({
    type: 'UPDATE_VEHICLE_SUCCESS',
    entity: vehicle,
  })
  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: {
      vehicleId: vehicle.id,
      editVehicleDetails: false,
    },
  })
  dispatch({
    type: 'SET_APPLICATION_STATE',
    payload: {
      //this may need to be set manually, or should always rely on this property vehicle.refinanceable?
      showVehicle: !vehicle.refinanceable,
      showProfile: vehicle.refinanceable,
    },
  })
}
export const syncRefiUser = (refiData, dispatch) => {
  const { user } = refiData

  dispatch({
    type: 'CREATE_USER',
    entity: {
      firstName: user.first_name,
      lastName: user.last_name,
      ...user,
    },
  })
  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: {
      userId: user.id,
      addressId: user.addresses[user.addresses.length - 1].id,
    },
  })
}

export const continueRefiStatuses = ['QUALIFIED', 'REVIEW', 'PENDING']

/**
 * Carries the CallTools vehicle's values into the wizard for a new deal.
 *
 * Stores attributes only, under its own key, and never sets
 * currentApplication.vehicleId: with a vehicleId the Vehicle step would post
 * `id: vehicle.id` (vehicleDetailSection#onPlateVinSubmit) and rewrite the
 * vehicle the customer's other deals sit on. Unbound, that submit creates a
 * vehicle instead. plateVinForm is the only reader.
 *
 * Keys are the vehicle entity's own field names, so the form reads them through
 * the mapping it already uses for a saved vehicle.
 */
// Fields the form treats as text. parseQuery coerces every all-digit param to a
// Number, so a RAM 1500, Fiat 500 or Porsche 911 would arrive as a number and
// blow up formatLabel's `.split` in plateVinForm. Keep them the strings a
// vehicle entity would have carried.
const PREFILL_TEXT_FIELDS = [
  'make',
  'model',
  'vin',
  'plateNumber',
  'plateState',
  'condition',
  'transmission',
]

/**
 * Marks the application as starting a deal rather than resuming one.
 *
 * Set by both entry points that mean "start a deal" — the CallTools deals
 * modal's New Deal button and Add a Vehicle — and read when the package is
 * created. Without it ProductPackages::Create reuses any non-terminal package
 * on this user+vehicle, reopening a deal the agent did not ask for.
 */
export const markFreshDealInRefiApp = () => (dispatch) => {
  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: { fresh: true },
  })
}

export const prefillVehicleValuesInRefiApp = (values) => (dispatch) => {
  const prefillVehicle = pick(values, [
    'year',
    'make',
    'model',
    'vin',
    'plateNumber',
    'plateState',
    'mileage',
    'condition',
    'transmission',
    'selectedVehicleTrimId',
  ])

  if (Object.keys(prefillVehicle).length === 0) return

  PREFILL_TEXT_FIELDS.forEach((field) => {
    if (prefillVehicle[field] != null) {
      prefillVehicle[field] = String(prefillVehicle[field])
    }
  })

  dispatch({
    type: 'SET_REFI_APPLICATION_DATA',
    payload: { prefillVehicle },
  })
}

export const prefillUserInRefiApp =
  (userId: string) => (dispatch, getState) => {
    const user = selectUser(getState().users, userId)
    if (!user?.id) return
    const lastAddress = user.addresses?.[user.addresses.length - 1]
    dispatch({ type: 'CREATE_USER', entity: user })
    dispatch({
      type: 'SET_REFI_APPLICATION_DATA',
      payload: {
        userId: user.id,
        ...(lastAddress ? { addressId: lastAddress.id } : {}),
      },
    })
  }

/**
 * Receives the refi object, expecting it to have the same structure as the admin/refis endpoint,
 * parses the content of the refi object, identifies where the refi has 'stopped',
 * populates the redux state in order to sync the refiApp feature data with the parsed data,
 * syncs the models: vehicle, user, garage, refi and the creditApplication state to allow the user
 * to continue a refi application at the correct screen and correct step through the application flow
 *
 * @param {Refi} refiData The refi application data to be loaded.
 * @returns {Promise}
 */
export const continueRefi = (refiData) => async (dispatch) => {
  if (refiData === null || refiData === undefined) {
    dispatch(displayNotification('Refi data not found to be continued'))
    return
  }

  const { vehicle, user, refi } = refiData
  if (
    vehicle === null ||
    vehicle === undefined ||
    user === null ||
    user === undefined ||
    refi === null ||
    refi === undefined
  ) {
    dispatch(
      displayNotification('Error: Refi missing data and cannot continue')
    )
    return
  }

  if (!continueRefiStatuses.includes(String(refi.status).toUpperCase())) {
    dispatch(
      displayNotification(
        `Refi (${refi.id}) status not allowed to continue: ${refi.status}`
      )
    )
    dispatch(displayNotification(JSON.stringify(continueRefiStatuses)))
    return
  }

  //sync the vehicle data from this refi
  syncRefiVehicle(refiData, dispatch)

  //sync the user data from this refi
  syncRefiUser(refiData, dispatch)

  //garage data needs to be fetched to be complete and sync correctly
  const garageEntry = {
    user_id: user.id,
    vehicle_id: vehicle.id,
    category: 'user',
  }
  dispatch(findOrCreateGarageEntry(garageEntry))
    .then(() => {
      // business config needs to be already fetched no matter what
      // a screen realod can break the process if this is not fetched at this point
      return dispatch(fetchBusinessConfig())
    })
    .then(() => {
      // sync the rest of refi data with the expected properties
      //needed by the relevant screens and components (tradelines, products, refiSummary, etc)
      const refiProperties = pick(refi, [
        'cash_back_amount',
        'cash_back_options',
        'coApplicant',
        'currentLoan',
        'interest_rate',
        'current_loan_payoff',
        'current_loan_term_remaining',
        'current_loan_date',
        'fees',
        'id',
        'monthly_payment',
        'next_actions',
        'open_tradelines',
        'products',
        'membership_id',
        'status',
        'term',
        'todos',
        'vehicle',
        'total_amount',
      ])
      const refiEntity = {
        applicant: {
          id: user.id,
          name: `${user.first_name} ${user.last_name} `,
        },
        ...refiProperties,
      }
      dispatch({ type: 'TOGGLE_PROFILE_UI_STATE' })
      dispatch({
        type: 'CREATE_REFI',
        entity: refiEntity,
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          refiId: refi.id,
        },
      })
      dispatch({ type: 'TOGGLE_CREDIT_UI_STATE' })
      // no tradeline means that a tradeline needs to be choosen now
      if (refi.current_loan_tradeline_index === null && refi.term === null) {
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            creditApplicationStep: 'SelectTradeline',
          },
        })
        // if terms has been choosen, go to overview of the tradeline selection
      } else if (
        refi.current_loan_tradeline_index !== null &&
        refi.term === null
      ) {
        dispatch({
          type: 'SET_REFI_APPLICATION_DATA',
          payload: {
            selectedTradelineIndex: refi.current_loan_tradeline_index,
            creditApplicationStep: 'LoanOverviewComponent',
          },
        })
        //payments option are needed to sync the refi and tradeline selected previously
      } else {
        dispatch(setRefiAppCreditStepState('Loading'))
        dispatch(getRefiPaymentOptions(refi))
          .then(() => {
            //from this point we should be able to fetch products correctly
            return dispatch(createRefiProducts({ id: refi.id }))
          })
          .then(() => {
            const gapProductSelected = !!refi.products.find(
              (p) => p.product_type === 'gap' && p.selected_option_id
            )
            const noVscProductsSelected = refi.products.every(
              (p) => !(p.product_type === 'vsc' && p.selected_option_id)
            )

            if (gapProductSelected) {
              dispatch(setRefiAppCreditStepState('VscProducts'))
            } else if (noVscProductsSelected) {
              dispatch(setRefiAppCreditStepState('MembershipAndGapProduct'))
            } else {
              //if there is a tradeline_index, and selected products is not empty, refi just needs to be finalized
              dispatch(setRefiAppCreditStepState('ReviewAndConfirm'))
            }
          })
      }
    })
}
